/**
 * Agent API Client
 */
import { apiClient } from './client'
import { useAuthStore } from '@/stores/authStore'

export interface AgentMessage {
  role: 'user' | 'agent' | 'system'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export interface SessionContext {
  session_id: string
  messages: AgentMessage[]
  knowledge_chunks: { chunk_id: string; domain: string; content: string; relevance_score?: number }[]
  active_entities: { entity_type: string; entity_id: string; display_text?: string }[]
  page_context: { path: string; entity_type?: string; entity_id?: string }
  user_role: string
  company_id: string
}

export type InteractionMode = 'text' | 'voice_browser' | 'sms' | 'voice_twilio'

export interface ToolDefinition {
  name: string
  description: string
  category: string
  required_permissions: string[]
}

export interface AuditFilters {
  user_id?: string
  session_id?: string
  tool_name?: string
  from?: string
  to?: string
  page?: number
  limit?: number
}

export const agentAPI = {
  /** SSE streaming chat — returns a fetch Response for manual SSE parsing */
  sendMessage: async (message: string, sessionContext: SessionContext, mode: InteractionMode = 'text') => {
    const token = useAuthStore.getState().accessToken
    const baseURL = import.meta.env.VITE_API_URL || ''
    const res = await fetch(`${baseURL}/api/v3/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, session_context: sessionContext, interaction_mode: mode }),
    })
    return res
  },

  cancelResponse: (sessionId: string) =>
    apiClient.post('/agent/cancel', { session_id: sessionId }),

  getToolCatalog: () =>
    apiClient.get<{ data: ToolDefinition[] }>('/agent/tools'),

  getAudit: (filters: AuditFilters) =>
    apiClient.get<{ data: unknown[]; pagination: { page: number; limit: number } }>('/agent/audit', filters),

  searchKnowledge: (query: string, domain?: string, limit?: number) =>
    apiClient.get<{ data: unknown[] }>('/agent/knowledge', { query, domain, limit }),

  initiateCall: (to: string, context?: Record<string, unknown>) =>
    apiClient.post('/agent/call', { to, context }),

  getCallStatus: (callId: string) =>
    apiClient.get(`/agent/call/${callId}`),
}
